import { browser } from '$app/environment';
import { init, register, locale, waitLocale } from 'svelte-i18n';

const defaultLocale = 'en';

/**
 * The languages the app is written in.
 *
 * # No flags
 *
 * A flag names a country, not a language: 🇬🇧 for English tells a Swiss
 * customer that this is Britain's language rather than the one the app is
 * written in, and there is no flag that means German without also meaning
 * Germany — in a product sold in Switzerland, to German speakers, that is the
 * wrong claim to be making twice over.
 *
 * # Why the list is here
 *
 * Two controls offer it — the account menu on desktop, the drawer on mobile —
 * and it sat in the drawer's component, where the menu could not reach it. A
 * second copy is a second list to add a language to, and the one that gets
 * forgotten offers a language the other does not.
 */
export const languages = [
  { code: 'en', short: 'EN', name: 'English' },
  { code: 'de', short: 'DE', name: 'Deutsch' }
] as const;

// Register all translation files
register('en', () => import('./locales/en.json'));
register('de', () => import('./locales/de.json'));

// Get locale from browser or default
function getLocaleFromNavigator() {
  if (!browser) return defaultLocale;

  const stored = localStorage.getItem('locale');
  if (stored) return stored;

  const browserLang = window.navigator.language.toLowerCase();
  const supportedLocales: string[] = languages.map((language) => language.code);

  // Check exact match
  if (supportedLocales.includes(browserLang)) {
    return browserLang;
  }

  // Check language prefix (e.g., 'en-US' -> 'en')
  const langPrefix = browserLang.split('-')[0];
  if (supportedLocales.includes(langPrefix)) {
    return langPrefix;
  }

  return defaultLocale;
}

// Initialize i18n with the detected/stored locale
init({
  fallbackLocale: defaultLocale,
  initialLocale: getLocaleFromNavigator()
});

// Set locale and save to localStorage
export function setLocale(newLocale: string) {
  locale.set(newLocale);
  if (browser) {
    localStorage.setItem('locale', newLocale);
  }
}

// Export for use in components
export { locale, waitLocale };
